import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FileSignature, Loader2, FileText, FileDown, Euro } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { WeekGrid, emptyWeek } from "./WeekScheduleGrid";

const QUALIFICA_DEFAULT = "PERSONALE NON QUALIFICATO ADDETTO ALLA PULIZIA NEI SERVIZI DI ALLOGGIO E NELLE NAVI";
const QUALIFICA_TRASFORMAZIONE = "OPERAI ADDETTI AI SERVIZI DI IGIENE E PULIZIA";
const MANSIONE_DEFAULT = "Addetta al Riassetto e Rigoverno di Locali, Foresterie ed Assimilabili";

const fmtIt = (iso) => {
    if (!iso || typeof iso !== "string" || !iso.includes("-")) return iso || "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

const TIPO_COM = { assunzione: "Assunzione", proroga: "Proroga", trasformazione: "Trasformazione", cessazione: "Cessazione" };

const unilavLabel = (r) => {
    const tipo = TIPO_COM[r.tipo_comunicazione] || r.tipo_comunicazione || "UNILAV";
    const parts = [tipo];
    if (r.tipo_contratto) parts.push(r.tipo_contratto);
    if (r.data_inizio) parts.push(`dal ${fmtIt(r.data_inizio)}${r.data_fine ? ` al ${fmtIt(r.data_fine)}` : ""}`);
    else if (r.data_fine) parts.push(`fino al ${fmtIt(r.data_fine)}`);
    if (r.ore_settimanali) parts.push(`${r.ore_settimanali}h`);
    if (r.livello_inquadramento) parts.push(`${r.livello_inquadramento}° liv.`);
    const invio = r.parsed_data?.data_invio;
    if (invio) parts.push(`(UNILAV del ${fmtIt(invio)})`);
    return parts.join(" · ");
};


const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

// Pick the contract template matching the UNILAV communication/contract type
const pickTemplate = (rec, list) => {
    const usable = list.filter((t) => (t.kind || "template") === "template");
    const tipoCom = (rec.tipo_comunicazione || "").toLowerCase();
    const tipoCon = (rec.tipo_contratto || "").toLowerCase();
    const byName = (pred) => usable.filter((t) => pred(t.name.toLowerCase()));
    let matches = [];
    if (tipoCom === "trasformazione") matches = byName((n) => n.includes("trasformazion"));
    else if (tipoCom === "proroga") matches = byName((n) => n.includes("proroga"));
    else if (tipoCon.includes("indeterminato")) matches = byName((n) => n.includes("indeterminato") && !n.includes("trasformazion"));
    else if (tipoCon.includes("determinato")) matches = byName((n) => n.includes("determinato") && !n.includes("indeterminato") && !n.includes("trasformazion") && !n.includes("proroga"));
    return matches.length === 1 ? matches[0].id : "";
};

export const GenerateContractDialog = ({ open, onOpenChange, employee, onSaved }) => {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [format, setFormat] = useState("docx");
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);

    const [livello, setLivello] = useState("");
    const [ore, setOre] = useState("");
    const [includeAnzianita, setIncludeAnzianita] = useState(false);
    const [pay, setPay] = useState(null);
    const [sede, setSede] = useState("");
    const [qualificaContratto, setQualificaContratto] = useState(QUALIFICA_DEFAULT);
    const [mansione, setMansione] = useState(MANSIONE_DEFAULT);
    const [decorrenza, setDecorrenza] = useState("");
    const [dataFine, setDataFine] = useState("");
    const [dataAssunzioneOrig, setDataAssunzioneOrig] = useState("");
    const [dataFineProroga, setDataFineProroga] = useState("");
    const [dataLettera, setDataLettera] = useState("");
    const [prova, setProva] = useState("8");
    const [settimane, setSettimane] = useState("1");
    const [sett1, setSett1] = useState(emptyWeek());
    const [sett2, setSett2] = useState(emptyWeek());
    const [includeAllegato, setIncludeAllegato] = useState(false);
    const [saveToDocs, setSaveToDocs] = useState(true);
    const [unilavInfo, setUnilavInfo] = useState(null);
    const [unilavRecords, setUnilavRecords] = useState([]);
    const [unilavQualifica, setUnilavQualifica] = useState("");
    const [unilavExtracting, setUnilavExtracting] = useState(false);
    const [qualifiche, setQualifiche] = useState([]);

    // Apply UNILAV data to the form. onlyIfEmpty preserves anything the user already typed.
    const applyUnilavRecord = useCallback((rec, tplList, { onlyIfEmpty = false } = {}) => {
        if (!rec) return;
        setUnilavInfo(rec);
        const put = (setter, v) => setter((cur) => (onlyIfEmpty && cur ? cur : v));
        if (rec.livello_inquadramento) put(setLivello, String(rec.livello_inquadramento).trim());
        if (rec.ore_settimanali) put(setOre, String(rec.ore_settimanali));
        if (rec.data_inizio) put(setDecorrenza, rec.data_inizio);
        if (rec.data_fine) put(setDataFine, rec.data_fine);
        const qual = (rec.qualifica || "").trim();
        if (qual && qual.toUpperCase() !== "DA DEFINIRE") setUnilavQualifica(qual.toUpperCase());
        const pd = rec.parsed_data || {};
        const sedeUnilav = [pd.sede_lavoro_indirizzo, pd.sede_lavoro_comune].filter(Boolean).join(", ");
        if (sedeUnilav) setSede((cur) => cur || sedeUnilav);
        const tid = pickTemplate(rec, tplList);
        if (tid) setTemplateId((cur) => (onlyIfEmpty && cur ? cur : tid));
    }, []);

    useEffect(() => {
        if (!open) return;
        (async () => {
            setLoading(true);
            try {
                const [tplRes, grpRes, uniRes, qualRes] = await Promise.all([
                    api.get("/contract-templates"),
                    api.get("/groups").catch(() => ({ data: [] })),
                    api.get(`/unilav/records/${employee?.id}/all`).catch(() => ({ data: { records: [] } })),
                    api.get("/contract-templates/qualifiche").catch(() => ({ data: { qualifiche: [] } })),
                ]);
                setTemplates(tplRes.data);
                const usable = tplRes.data.filter((t) => (t.kind || "template") === "template");
                if (usable.length === 1) setTemplateId(usable[0].id);
                const names = (employee?.group_ids || [])
                    .map((gid) => grpRes.data.find((g) => g.id === gid)?.name)
                    .filter((n) => n && n.toUpperCase() !== "DIPENDENTI");
                if (names.length > 0) setSede(`\u201c${names[0]}\u201d`);
                setQualifiche(qualRes.data.qualifiche || []);
                const records = (uniRes.data.records || []).filter((r) => r.tipo_comunicazione !== "cessazione");
                setUnilavRecords(records);
                const rec = records[0];
                if (rec) {
                    applyUnilavRecord(rec, tplRes.data);
                } else if (employee?.id) {
                    // No saved record: AI-read the archived UNILAV in background
                    setUnilavExtracting(true);
                    api.get(`/unilav/records/${employee.id}/latest`, { params: { extract_if_missing: true }, timeout: 120000 })
                        .then((r) => {
                            if (r.data.record) {
                                setUnilavRecords([r.data.record]);
                                applyUnilavRecord(r.data.record, tplRes.data, { onlyIfEmpty: true });
                            }
                        })
                        .catch((e) => console.warn("Estrazione UNILAV per prefill fallita:", e?.message))
                        .finally(() => setUnilavExtracting(false));
                }
            } catch {
                toast.error("Errore nel caricamento dei modelli");
            } finally {
                setLoading(false);
            }
        })();
        setLivello("");
        setDecorrenza("");
        setDataFine("");
        setDataLettera("");
        setUnilavInfo(null);
        setUnilavRecords([]);
        setUnilavQualifica("");
        setTemplateId("");
        setOre(employee?.contratto_ore ? String(employee.contratto_ore) : "");
        setDataAssunzioneOrig(employee?.data_assunzione || "");
    }, [open, employee, applyUnilavRecord]);

    const tpl = useMemo(() => templates.find((t) => t.id === templateId), [templates, templateId]);

    useEffect(() => {
        if (!tpl) return;
        setQualificaContratto(
            unilavQualifica || (tpl.name.toLowerCase().includes("trasformazione") ? QUALIFICA_TRASFORMAZIONE : QUALIFICA_DEFAULT)
        );
    }, [tpl, unilavQualifica]);

    const selectable = useMemo(() => templates.filter((t) => (t.kind || "template") === "template"), [templates]);
    const hasAllegato = useMemo(() => templates.some((t) => t.kind === "allegato"), [templates]);
    const has = (key) => !!tpl?.placeholders?.includes(key);
    const hasPrefix = (prefix) => !!tpl?.placeholders?.some((p) => p.startsWith(prefix));
    const needsLivello = has("livello_label") || has("livello_num") || hasPrefix("retrib_");
    const needsOre = has("ore_settimanali");
    const needsOrari = hasPrefix("o1_");
    const isHourly = hasPrefix("retrib_h_") && !has("retrib_totale");

    useEffect(() => {
        if (!livello) {
            setPay(null);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get("/contract-templates/compute-pay", {
                    params: { livello, ore: ore || 40, anzianita: includeAnzianita },
                });
                setPay(data);
            } catch {
                setPay(null);
            }
        })();
    }, [livello, ore, includeAnzianita]);

    const updateWeek = (setter) => (day, part, value) =>
        setter((w) => ({ ...w, [day]: { ...w[day], [part]: value } }));

    const handleGenerate = async () => {
        if (!templateId) {
            toast.error("Seleziona un modello");
            return;
        }
        if (needsLivello && !livello) {
            toast.error("Seleziona il livello di inquadramento");
            return;
        }
        if ((has("decorrenza_estesa") || has("decorrenza_breve") || has("decorrenza_breve2")) && !decorrenza) {
            toast.error("Inserisci la data di decorrenza");
            return;
        }
        setGenerating(true);
        try {
            const orari = { sett1 };
            if (settimane === "2") orari.sett2 = sett2;
            const extra = {
                livello,
                ore_settimanali: ore || undefined,
                include_anzianita: includeAnzianita,
                sede_lavoro: sede,
                qualifica_contratto: qualificaContratto,
                mansione_ccnl: mansione,
                decorrenza,
                data_fine: dataFine,
                data_assunzione_orig: dataAssunzioneOrig,
                data_fine_proroga: dataFineProroga,
                data_lettera: dataLettera,
                periodo_prova_giorni: prova,
                settimane: Number(settimane),
                orari,
            };
            const res = await api.post(
                `/contract-templates/${templateId}/generate`,
                { employee_id: employee.id, format, extra, include_allegato: includeAllegato, save_to_documents: saveToDocs },
                { responseType: "blob" }
            );
            const ext = format === "pdf" ? "pdf" : "docx";
            downloadBlob(res.data, `Contratto_${employee.cognome}_${employee.nome}.${ext}`);
            if (saveToDocs) {
                toast.success("Contratto generato e salvato nei documenti del dipendente");
                onSaved && onSaved();
            } else {
                toast.success("Contratto generato");
            }
            onOpenChange(false);
        } catch (e) {
            let msg = "Errore nella generazione del contratto";
            try {
                const text = await e.response?.data?.text?.();
                if (text) msg = JSON.parse(text).detail || msg;
            } catch { /* keep default */ }
            toast.error(msg);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[92vh] w-[96vw] overflow-y-auto overflow-x-hidden sm:max-w-5xl" data-testid="generate-contract-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2 text-[#376DB3]">
                        <FileSignature className="h-5 w-5" />
                        Genera contratto
                    </DialogTitle>
                    <DialogDescription>
                        Compila un modello Word con i dati di {employee?.cognome} {employee?.nome} e scaricalo.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="min-w-0 space-y-4" data-testid="generate-left-column">
                    <div>
                        <Label>Modello di contratto</Label>
                        {!loading && selectable.length === 0 ? (
                            <p className="mt-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                Nessun modello caricato.{" "}
                                <Link to="/modelli-contratto" className="font-semibold underline" data-testid="go-to-templates-link">
                                    Vai a Modelli contratto
                                </Link>{" "}
                                per caricare i tuoi file Word.
                            </p>
                        ) : (
                            <div className="mt-1.5">
                                <Select value={templateId} onValueChange={setTemplateId}>
                                    <SelectTrigger data-testid="generate-template-select">
                                        <SelectValue placeholder={loading ? "Caricamento…" : "Seleziona il modello"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {selectable.map((t) => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    {unilavExtracting && (
                        <div className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="unilav-extracting-badge">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Sto leggendo l'UNILAV archiviato per precompilare i dati dell'assunzione…
                        </div>
                    )}

                    {unilavRecords.length > 1 && (
                        <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2" data-testid="unilav-source-block">
                            <Label className="text-xs text-sky-900">Preleva i dati da (il dipendente ha {unilavRecords.length} UNILAV)</Label>
                            <Select
                                value={unilavInfo?.id || ""}
                                onValueChange={(id) => {
                                    const rec = unilavRecords.find((r) => r.id === id);
                                    if (rec) applyUnilavRecord(rec, templates);
                                }}
                            >
                                <SelectTrigger className="mt-1 h-8 bg-white text-xs" data-testid="unilav-source-select">
                                    <SelectValue placeholder="Scegli la comunicazione UNILAV" />
                                </SelectTrigger>
                                <SelectContent>
                                    {unilavRecords.map((r) => (
                                        <SelectItem key={r.id} value={r.id} data-testid={`unilav-source-option-${r.id}`}>
                                            {unilavLabel(r)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {unilavInfo && (
                        <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="unilav-prefill-badge">
                            <b>Dati precompilati dall'UNILAV</b>
                            {unilavInfo.tipo_comunicazione ? ` di ${unilavInfo.tipo_comunicazione}` : ""}
                            {unilavInfo.data_inizio ? ` (decorrenza ${fmtIt(unilavInfo.data_inizio)})` : ""}
                            {unilavInfo.data_fine ? ` fino al ${fmtIt(unilavInfo.data_fine)}` : ""}
                            {" — verifica e completa i campi."}
                        </div>
                    )}

                    {tpl && (
                        <>
                            {(needsLivello || needsOre) && (
                                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Inquadramento e retribuzione</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {needsLivello && (
                                            <div>
                                                <Label className="text-xs">Livello CCNL</Label>
                                                <Select value={livello} onValueChange={setLivello}>
                                                    <SelectTrigger className="mt-1 h-9" data-testid="generate-livello-select">
                                                        <SelectValue placeholder="Livello" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {["1", "2", "3", "4"].map((l) => (
                                                            <SelectItem key={l} value={l}>{l}° livello</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                        {needsOre && (
                                            <div>
                                                <Label className="text-xs">Ore settimanali</Label>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    max="40"
                                                    step="0.5"
                                                    value={ore}
                                                    onChange={(e) => setOre(e.target.value)}
                                                    className="mt-1 h-9"
                                                    data-testid="generate-ore-input"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-2 flex items-center gap-2">
                                        <Switch checked={includeAnzianita} onCheckedChange={setIncludeAnzianita} data-testid="generate-anzianita-switch" />
                                        <span className="text-xs text-slate-600">Includi anzianità forfettaria (di norma esclusa)</span>
                                    </div>
                                    {pay && (
                                        <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs" data-testid="generate-pay-preview">
                                            <p className="mb-1 flex items-center gap-1 font-semibold text-emerald-800">
                                                <Euro className="h-3 w-3" /> Retribuzione {isHourly ? "oraria" : "mensile"} (CCNL dal 01/05/2026)
                                            </p>
                                            {(() => {
                                                const p = isHourly ? pay.hourly : pay.monthly;
                                                return (
                                                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono-numbers text-emerald-900">
                                                        <span>Paga base: € {p.paga_base}</span>
                                                        <span>Contingenza: € {p.contingenza}</span>
                                                        <span>E.D.R.: € {p.edr}</span>
                                                        {includeAnzianita && <span>Anzianità: € {p.anzianita}</span>}
                                                        <span className="font-bold">Totale: € {p.totale}</span>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dati del contratto</p>
                                {has("qualifica_contratto") && (
                                    <div>
                                        <Label className="text-xs">Qualifica (intestazione, MAIUSCOLO)</Label>
                                        <Input value={qualificaContratto} onChange={(e) => setQualificaContratto(e.target.value)} list="qualifiche-suggestions" className="mt-1 h-9" data-testid="generate-qualifica-input" />
                                    </div>
                                )}
                                {has("mansione_ccnl") && (
                                    <div>
                                        <Label className="text-xs">Qualifica/Mansione CCNL</Label>
                                        <Input value={mansione} onChange={(e) => setMansione(e.target.value)} list="mansioni-suggestions" className="mt-1 h-9" data-testid="generate-mansione-input" />
                                    </div>
                                )}
                                {has("sede_lavoro") && (
                                    <div>
                                        <Label className="text-xs">Luogo di prestazione</Label>
                                        <Input
                                            value={sede}
                                            onChange={(e) => setSede(e.target.value)}
                                            placeholder='Es. “I Turchesi Club Village” in Viale dei Pini, 74010 Castellaneta Marina (TA)'
                                            className="mt-1 h-9"
                                            data-testid="generate-sede-input"
                                        />
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Decorrenza</Label>
                                        <Input type="date" value={decorrenza} onChange={(e) => setDecorrenza(e.target.value)} className="mt-1 h-9" data-testid="generate-decorrenza-input" />
                                    </div>
                                    {(has("data_fine_breve") || has("data_fine_estesa")) && (
                                        <div>
                                            <Label className="text-xs">Data fine contratto</Label>
                                            <Input type="date" value={dataFine} onChange={(e) => setDataFine(e.target.value)} className="mt-1 h-9" data-testid="generate-datafine-input" />
                                        </div>
                                    )}
                                    {(has("data_assunzione_orig_breve2") || has("data_assunzione_orig_breve")) && (
                                        <div>
                                            <Label className="text-xs">Assunzione originaria (T.D.)</Label>
                                            <Input type="date" value={dataAssunzioneOrig} onChange={(e) => setDataAssunzioneOrig(e.target.value)} className="mt-1 h-9" data-testid="generate-assorig-input" />
                                        </div>
                                    )}
                                    {(has("data_fine_proroga_breve2") || has("data_fine_proroga_breve")) && (
                                        <div>
                                            <Label className="text-xs">Fine proroga T.D.</Label>
                                            <Input type="date" value={dataFineProroga} onChange={(e) => setDataFineProroga(e.target.value)} className="mt-1 h-9" data-testid="generate-fineproroga-input" />
                                        </div>
                                    )}
                                    {has("periodo_prova_giorni") && (
                                        <div>
                                            <Label className="text-xs">Giorni di prova</Label>
                                            <Input type="number" min="0" value={prova} onChange={(e) => setProva(e.target.value)} className="mt-1 h-9" data-testid="generate-prova-input" />
                                        </div>
                                    )}
                                    <div>
                                        <Label className="text-xs">Data lettera (opzionale)</Label>
                                        <Input type="date" value={dataLettera} onChange={(e) => setDataLettera(e.target.value)} className="mt-1 h-9" data-testid="generate-datalettera-input" />
                                        <p className="mt-0.5 text-[10px] text-slate-500">Se vuota: primo giorno feriale prima della decorrenza.</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                  </div>

                  <div className="min-w-0 space-y-4" data-testid="generate-right-column">
                    {tpl && (
                        <>
                            {needsOrari && (
                                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                                    <div className="mb-2 flex items-center justify-between">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Orario di lavoro</p>
                                        <div className="flex gap-1">
                                            {["1", "2"].map((s) => (
                                                <button
                                                    key={s}
                                                    type="button"
                                                    onClick={() => setSettimane(s)}
                                                    className={`rounded-md border px-2.5 py-1 text-xs transition ${settimane === s ? "border-[#376DB3] bg-[#376DB3]/5 font-semibold text-[#376DB3]" : "border-slate-200 text-slate-600 hover:bg-white"}`}
                                                    data-testid={`generate-settimane-${s}`}
                                                >
                                                    {s === "1" ? "1 settimana" : "2 settimane alternate"}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-3">
                                        <WeekGrid
                                            title={settimane === "2" ? "1ª settimana" : "Settimana (uguale per tutte)"}
                                            week={sett1}
                                            onChange={updateWeek(setSett1)}
                                            testPrefix="orari-s1"
                                        />
                                        {settimane === "2" && (
                                            <WeekGrid title="2ª settimana" week={sett2} onChange={updateWeek(setSett2)} testPrefix="orari-s2" />
                                        )}
                                    </div>
                                    <p className="mt-1.5 text-[10px] text-slate-500">
                                        I giorni senza orario compariranno come «-» (riposo). I totali sono calcolati automaticamente.
                                    </p>
                                </div>
                            )}

                            {hasAllegato && (
                                <div className="flex items-center gap-2">
                                    <Switch checked={includeAllegato} onCheckedChange={setIncludeAllegato} data-testid="generate-allegato-switch" />
                                    <span className="text-xs text-slate-600">Allega il Regolamento disciplinare (prima assunzione)</span>
                                </div>
                            )}
                        </>
                    )}

                    <div>
                        <Label>Formato</Label>
                        <div className="mt-1.5 flex gap-2">
                            <button
                                type="button"
                                onClick={() => setFormat("docx")}
                                className={`flex flex-1 items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition ${format === "docx" ? "border-[#376DB3] bg-[#376DB3]/5 font-semibold text-[#376DB3]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                                data-testid="format-docx"
                            >
                                <FileText className="h-4 w-4" /> Word (.docx)
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormat("pdf")}
                                className={`flex flex-1 items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition ${format === "pdf" ? "border-[#376DB3] bg-[#376DB3]/5 font-semibold text-[#376DB3]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                                data-testid="format-pdf"
                            >
                                <FileDown className="h-4 w-4" /> PDF
                            </button>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                            Word è modificabile prima della firma; il PDF è pronto da stampare.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                        <Switch checked={saveToDocs} onCheckedChange={setSaveToDocs} data-testid="generate-save-docs-switch" />
                        <div>
                            <span className="text-xs font-medium text-slate-700">Salva nei documenti del dipendente</span>
                            <p className="text-[10px] text-slate-500">Il contratto verrà archiviato nella categoria «Contratto», pronto per la presa visione/firma dal portale.</p>
                        </div>
                    </div>
                  </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="generate-cancel">
                        Annulla
                    </Button>
                    <Button
                        onClick={handleGenerate}
                        disabled={generating || !templateId}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="generate-confirm"
                    >
                        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
                        Genera e scarica
                    </Button>
                </DialogFooter>

                <datalist id="mansioni-suggestions">
                    {[...new Set([MANSIONE_DEFAULT, ...qualifiche])].map((q) => (
                        <option key={q} value={q} />
                    ))}
                </datalist>
                <datalist id="qualifiche-suggestions">
                    {[...new Set([QUALIFICA_DEFAULT, QUALIFICA_TRASFORMAZIONE, ...qualifiche.map((q) => q.toUpperCase())])].map((q) => (
                        <option key={q} value={q} />
                    ))}
                </datalist>
            </DialogContent>
        </Dialog>
    );
};
